import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut, Wrench, Clock, CheckCircle, Upload, FileText,
  Eye, Edit, Truck, Package, DollarSign, Calendar,
  User, Building2, MapPin, AlertCircle, Download, Trash2, Plus, Search, Tag, Settings, Camera
} from 'lucide-react';
import Modal from './Modal';

interface TechnicalService {
  id: number;
  service_number: string;
  device_name: string;
  fault_description: string;
  status: string;
  priority: string;
  created_at: string;
  company: { name: string };
  location: { name: string };
  contact_person: { name: string };
  rejection_reason?: string;
  rejected_by?: number;
  rejected_at?: string;
}

interface CameraFault {
  id: number;
  camera_name: string;
  location_name?: string;
  fault_description: string;
  status: 'açık' | 'inceleniyor' | 'çözümlendi' | 'kapalı';
  reported_at: string;
  supplier_solution?: string;
  supplier_completed_at?: string;
  image_path?: string | null;
  camera_screenshot_path?: string | null;
}

interface FaultHistoryAction {
  id: number;
  action_type: string;
  old_status?: string;
  new_status?: string;
  description?: string;
  solution_text?: string;
  file_path?: string;
  supplier_user_name?: string;
  created_at: string;
}

const SupplierDashboard: React.FC = () => {
  const [services, setServices] = useState<TechnicalService[]>([]);
  const [completedServices, setCompletedServices] = useState<TechnicalService[]>([]);
  const [cameraFaults, setCameraFaults] = useState<CameraFault[]>([]);
  const [cameraFaultsStats, setCameraFaultsStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0 });
  const [cameraFaultsStatusFilter, setCameraFaultsStatusFilter] = useState('active');
  const [hasCameraCategory, setHasCameraCategory] = useState(false);
  const [dashboardImageModal, setDashboardImageModal] = useState<string | null>(null);
  const [showFaultHistoryModal, setShowFaultHistoryModal] = useState(false);
  const [selectedFaultForHistory, setSelectedFaultForHistory] = useState<CameraFault | null>(null);
  const [faultHistoryActions, setFaultHistoryActions] = useState<FaultHistoryAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState<TechnicalService | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showWorkModal, setShowWorkModal] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const navigate = useNavigate();

  const [statusData, setStatusData] = useState({
    status: '',
    description: ''
  });

  const [quoteData, setQuoteData] = useState({
    quote_amount: '',
    quote_currency: 'TRY',
    description: '',
    quote_file: null as File | null
  });

  const [workDevices, setWorkDevices] = useState([
    {
      device_name: '',
      device_serial: '',
      device_model: '',
      fault_description: '',
      work_performed: '',
      labor_hours: 0,
      labor_cost: 0,
      is_free_labor: false,
      parts: [
        {
          part_name: '',
          part_code: '',
          quantity: 1,
          unit_price: 0,
          total_price: 0
        }
      ]
    }
  ]);
  
  const [workData, setWorkData] = useState({
    currency: 'TRY',
    work_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [partSuggestions, setPartSuggestions] = useState<{[key: string]: string[]}>({});
  const [showSuggestions, setShowSuggestions] = useState<{[key: string]: boolean}>({});
  const [quoteStatus, setQuoteStatus] = useState<{[key: number]: boolean}>({});
  const [expandedDevices, setExpandedDevices] = useState<{[key: number]: boolean}>({});
  const [showLabelSettingsModal, setShowLabelSettingsModal] = useState(false);
  const [labelSettings, setLabelSettings] = useState({
    width: 100,
    height: 75,
    orientation: 'portrait',
    fontSize: 8,
    margin: 2
  });

  useEffect(() => {
    const supplierUser = localStorage.getItem('supplier_user');
    if (!supplierUser) {
      navigate('/supplier-login');
      return;
    }
    const userData = JSON.parse(supplierUser);
    setUser(userData);
    checkSupplierCategory(userData.supplier_id);
    loadServices();
  }, [navigate]);

  const loadServices = async () => {
    try {
      const token = localStorage.getItem('supplier_token');
      
      // Aktif servisleri yükle
      const response = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/assigned-services`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      
      if (response.ok) {
        const data = await response.json();
        setServices(data);
        
        // Tamamlanan servisleri yükle
        const completedResponse = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/completed-services`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (completedResponse.ok) {
          const completedData = await completedResponse.json();
          setCompletedServices(completedData);
        }
        
        // Her servis için teklif durumunu kontrol et
        const quoteStatuses: {[key: number]: boolean} = {};
        for (const service of data) {
          try {
            const quoteResponse = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${service.id}/work-details`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            if (quoteResponse.ok) {
              const workDetails = await quoteResponse.json();
              quoteStatuses[service.id] = workDetails.length > 0;
            }
          } catch (error) {
            console.error(`Error checking quote status for service ${service.id}:`, error);
          }
        }
        setQuoteStatus(quoteStatuses);
      } else {
        const errorData = await response.text();
        console.error('Response error:', errorData);
      }
    } catch (error) {
      console.error('Load services error:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSupplierCategory = async (supplierId: number) => {
    try {
      const token = localStorage.getItem('supplier_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/supplier-auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const serviceCategories = JSON.parse(data.service_categories || '[]');
        const hasCameraService = serviceCategories.includes('Güvenlik Kamera Sistemleri');
        setHasCameraCategory(hasCameraService);

        if (hasCameraService) {
          loadCameraFaults();
        }
      }
    } catch (error) {
      console.error('Error checking supplier category:', error);
    }
  };

  const loadCameraFaults = async () => {
    try {
      const token = localStorage.getItem('supplier_token');

      // Atanan kamera arızalarını yükle
      const response = await fetch(`${import.meta.env.VITE_API_URL}/supplier-camera-faults/assigned-faults`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCameraFaults(data.data || []);

        // Dashboard istatistiklerini yükle
        const statsResponse = await fetch(`${import.meta.env.VITE_API_URL}/supplier-camera-faults/dashboard-stats`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setCameraFaultsStats(statsData);
        }
      }
    } catch (error) {
      console.error('Error loading camera faults:', error);
    }
  };

  const fetchFaultHistory = async (fault: CameraFault) => {
    try {
      const token = localStorage.getItem('supplier_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/supplier-camera-faults/fault/${fault.id}/history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setFaultHistoryActions(result.data?.actions || []);
        setSelectedFaultForHistory(fault);
        setShowFaultHistoryModal(true);
      }
    } catch (error) {
      console.error('Error fetching fault history:', error);
    }
  };

  const calculateDaysBetween = (startDate: string, endDate?: string) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getActionTypeLabel = (actionType: string) => {
    const labels: Record<string, string> = {
      'status_change': 'Durum Değişikliği',
      'image_upload': 'Görsel Yüklendi',
      'solution_added': 'Çözüm Eklendi',
      'note_added': 'Not Eklendi',
      'assigned': 'Tedarikçiye Atandı'
    };
    return labels[actionType] || actionType;
  };

  const handleLogout = () => {
    localStorage.removeItem('supplier_token');
    localStorage.removeItem('supplier_user');
    navigate('/supplier-login');
  };

  const handleStatusUpdate = async () => {
    try {
      const token = localStorage.getItem('supplier_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${selectedService?.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(statusData)
      });

      if (response.ok) {
        setShowStatusModal(false);
        loadServices();
        alert('Durum güncellendi');
      }
    } catch (error) {
      console.error('Status update error:', error);
    }
  };

  const handleQuoteUpload = async () => {
    try {
      const token = localStorage.getItem('supplier_token');
      const formData = new FormData();
      
      if (quoteData.quote_file) {
        formData.append('quote_file', quoteData.quote_file);
      }
      formData.append('quote_amount', quoteData.quote_amount);
      formData.append('quote_currency', quoteData.quote_currency);
      formData.append('description', quoteData.description);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${selectedService?.id}/quote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        setShowQuoteModal(false);
        loadServices();
        alert('Teklif yüklendi');
      }
    } catch (error) {
      console.error('Quote upload error:', error);
    }
  };

  // Parça önerilerini getir
  const getPartSuggestions = async (query: string, type: 'name' | 'code', key: string) => {
    if (query.length < 3) {
      setPartSuggestions(prev => ({ ...prev, [key]: [] }));
      setShowSuggestions(prev => ({ ...prev, [key]: false }));
      return;
    }
    
    try {
      const token = localStorage.getItem('supplier_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/parts/suggestions?query=${encodeURIComponent(query)}&type=${type}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const suggestions = await response.json();
        setPartSuggestions(prev => ({ ...prev, [key]: suggestions }));
        setShowSuggestions(prev => ({ ...prev, [key]: suggestions.length > 0 }));
      }
    } catch (error) {
      console.error('Part suggestions error:', error);
    }
  };
  
  const selectPartSuggestion = (suggestion: string, deviceIndex: number, partIndex: number, field: 'part_name' | 'part_code') => {
    updatePart(deviceIndex, partIndex, field, suggestion);
    const key = `${deviceIndex}-${partIndex}-${field}`;
    setShowSuggestions(prev => ({ ...prev, [key]: false }));
  };
  
  const toggleDeviceExpanded = (index: number) => {
    setExpandedDevices(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  // Cihaz ekleme/çıkarma fonksiyonları kaldırıldı - sadece mevcut cihazlar için işlem yapılacak
  
  const updateDevice = (deviceIndex: number, field: string, value: any) => {
    const updatedDevices = [...workDevices];
    updatedDevices[deviceIndex] = { ...updatedDevices[deviceIndex], [field]: value };
    setWorkDevices(updatedDevices);
  };
  
  const addPart = (deviceIndex: number) => {
    const updatedDevices = [...workDevices];
    updatedDevices[deviceIndex].parts.push({
      part_name: '',
      part_code: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0
    });
    setWorkDevices(updatedDevices);
  };
  
  const removePart = (deviceIndex: number, partIndex: number) => {
    const updatedDevices = [...workDevices];
    if (updatedDevices[deviceIndex].parts.length > 1) {
      updatedDevices[deviceIndex].parts = updatedDevices[deviceIndex].parts.filter((_, i) => i !== partIndex);
      setWorkDevices(updatedDevices);
    }
  };
  
  const updatePart = (deviceIndex: number, partIndex: number, field: string, value: any) => {
    const updatedDevices = [...workDevices];
    updatedDevices[deviceIndex].parts[partIndex] = {
      ...updatedDevices[deviceIndex].parts[partIndex],
      [field]: value
    };
    
    // Otomatik toplam hesaplama
    if (field === 'quantity' || field === 'unit_price') {
      const part = updatedDevices[deviceIndex].parts[partIndex];
      part.total_price = (parseFloat(part.quantity) || 0) * (parseFloat(part.unit_price) || 0);
    }
    
    setWorkDevices(updatedDevices);
  };
  
  const calculateDeviceTotal = (device: any) => {
    const partsTotal = device.is_free_parts ? 0 : device.parts.reduce((sum: number, part: any) => sum + (parseFloat(part.total_price) || 0), 0);
    const laborCost = device.is_free_labor ? 0 : (parseFloat(device.labor_cost) || 0);
    return partsTotal + laborCost;
  };
  
  const calculateGrandTotal = () => {
    return workDevices.reduce((sum, device) => sum + calculateDeviceTotal(device), 0);
  };
  
  const handleWorkDetailAdd = async () => {
    try {
      const token = localStorage.getItem('supplier_token');
      
      // İş detayını kaydet
      const workResponse = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${selectedService?.id}/work-detail-advanced`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          devices: workDevices,
          ...workData
        })
      });

      if (workResponse.ok) {
        // Durumu 'İşleme Alındı' olarak güncelle
        const statusResponse = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${selectedService?.id}/status`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: 'isleme-alindi',
            description: 'İş detayları eklendi ve işleme alındı'
          })
        });
        
        setShowWorkModal(false);
        alert('İş detayı eklendi ve durum güncellendi');
        loadServices();
      }
    } catch (error) {
      console.error('Work detail add error:', error);
    }
  };

  const handleDeleteQuote = async (serviceId: number) => {
    if (!confirm('Teklifi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('supplier_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${serviceId}/quote`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Teklif silindi');
        loadServices();
      } else {
        alert('Teklif silinirken hata oluştu');
      }
    } catch (error) {
      console.error('Delete quote error:', error);
      alert('Teklif silinirken hata oluştu');
    }
  };

  const handleWorkDetailAndQuote = async () => {
    try {
      const token = localStorage.getItem('supplier_token');
      
      // İş detayını kaydet
      const workResponse = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${selectedService?.id}/work-detail-advanced`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          devices: workDevices,
          ...workData
        })
      });

      if (!workResponse.ok) {
        throw new Error('İş detayı kaydedilemedi');
      }

      // Teklif ver veya güncelle
      const grandTotal = calculateGrandTotal();
      const isUpdate = quoteStatus[selectedService?.id || 0];
      const method = isUpdate ? 'PUT' : 'POST';
      const url = `${import.meta.env.VITE_API_URL}/supplier-services/service/${selectedService?.id}/quote`;
      
      const quoteResponse = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quote_amount: grandTotal.toString(),
          quote_currency: workData.currency,
          description: `İş detayı bazlı teklif - ${workDevices.length} cihaz için toplam maliyet`,
          work_based: 'true'
        })
      });

      if (quoteResponse.ok) {
        // Durumu 'Teklif Yüklendi' olarak güncelle
        const statusResponse = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${selectedService?.id}/status`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: 'teklif-yuklendi',
            description: 'İş detayları eklendi ve teklif yüklendi'
          })
        });
        
        setShowWorkModal(false);
        alert(isUpdate ? 'İş detayı ve teklif güncellendi' : 'İş detayı kaydedildi ve teklif verildi');
        loadServices();
      } else {
        const errorText = await quoteResponse.text();
        console.error('Quote error:', errorText);
        alert('İş detayı kaydedildi ancak teklif verilemedi: ' + errorText);
      }
    } catch (error) {
      console.error('Work detail and quote error:', error);
      alert('Hata: ' + error.message);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'servise-gonderildi': return 'bg-primary/20 text-primary-dark';
      case 'isleme-alindi': return 'bg-yellow-100 text-yellow-800';
      case 'teklif-yuklendi': return 'bg-purple-100 text-purple-800';
      case 'siparis-olusturuldu': return 'bg-indigo-100 text-indigo-800';
      case 'onarildi-gonderildi': return 'bg-green-100 text-green-800';
      case 'tamamlandi': return 'bg-green-100 text-green-800';
      case 'reddedildi': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'servise-gonderildi': return 'Servise Gönderildi';
      case 'isleme-alindi': return 'İşleme Alındı';
      case 'teklif-yuklendi': return 'Teklif Yüklendi';
      case 'siparis-olusturuldu': return 'Sipariş Oluşturuldu';
      case 'onarildi-gonderildi': return 'Onarıldı ve Gönderildi';
      case 'tamamlandi': return 'Tamamlandı';
      case 'reddedildi': return 'Reddedildi';
      default: return status;
    }
  };

  const supplierStatusOptions = [
    { value: 'isleme-alindi', label: 'İşleme Alındı' },
    { value: 'onarildi-gonderildi', label: 'Onarıldı ve Gönderildi' }
  ];
  
  const allStatusOptions = [
    { value: 'servise-gonderildi', label: 'Servise Gönderildi' },
    { value: 'isleme-alindi', label: 'İşleme Alındı' },
    { value: 'teklif-yuklendi', label: 'Teklif Yüklendi' },
    { value: 'siparis-olusturuldu', label: 'Sipariş Oluşturuldu' },
    { value: 'onarildi-gonderildi', label: 'Onarıldı ve Gönderildi' },
    { value: 'tamamlandi', label: 'Tamamlandı' }
  ];
  
  const priorityOptions = [
    { value: 'dusuk', label: 'Düşük' },
    { value: 'normal', label: 'Normal' },
    { value: 'yuksek', label: 'Yüksek' },
    { value: 'acil', label: 'Acil' }
  ];
  
  const filteredServices = (filterStatus === 'tamamlandi' ? completedServices : services).filter(service => {
    const searchLower = String(searchTerm || '').toLowerCase();
    const matchesSearch = String(service.service_number || '').toLowerCase().includes(searchLower) ||
                         String(service.device_name || '').toLowerCase().includes(searchLower) ||
                         String(service.fault_description || '').toLowerCase().includes(searchLower) ||
                         service.company?.name.toLowerCase().includes(searchLower) ||
                         service.location?.name.toLowerCase().includes(searchLower) ||
                         (service.serial_number && String(service.serial_number || '').toLowerCase().includes(searchLower)) ||
                         (service.inventory_number && String(service.inventory_number || '').toLowerCase().includes(searchLower)) ||
                         service.contact_person?.name.toLowerCase().includes(searchLower);
    const matchesStatus = filterStatus === 'all' || filterStatus === 'tamamlandi' || service.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || service.priority === filterPriority;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-700">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Wrench className="h-8 w-8 text-purple-600 mr-3" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Tedarikçi Paneli</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">{user?.supplier_name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700 dark:text-gray-300">Hoş geldin, {user?.name}</span>
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Çıkış
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setFilterStatus('all')}
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-primary" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Toplam Servis</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{services.length}</p>
              </div>
            </div>
          </div>

          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setFilterStatus('isleme-alindi')}
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">İşlemde</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {services.filter(s => s.status === 'isleme-alindi').length}
                </p>
              </div>
            </div>
          </div>

          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setFilterStatus('teklif-yuklendi')}
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Teklif Yüklendi</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {services.filter(s => s.status === 'teklif-yuklendi').length}
                </p>
              </div>
            </div>
          </div>

          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setFilterStatus('onarildi-gonderildi')}
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Truck className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Onarıldı</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {services.filter(s => s.status === 'onarildi-gonderildi').length}
                </p>
              </div>
            </div>
          </div>

          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setFilterStatus('tamamlandi')}
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Tamamlanan</p>
                <p className="text-2xl font-bold text-green-600">
                  {completedServices.length}
                </p>
              </div>
            </div>
          </div>

          {/* Uyarı Kartı */}
          <div 
            className="bg-red-50 border border-red-200 rounded-lg shadow p-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setFilterStatus('servise-gonderildi')}
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-red-600">Geciken Servisler</p>
                <p className="text-2xl font-bold text-red-700">
                  {services.filter(s => {
                    const sentDate = new Date(s.updated_at || s.created_at);
                    const oneWeekAgo = new Date();
                    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                    return s.status === 'servise-gonderildi' && sentDate < oneWeekAgo;
                  }).length}
                </p>
                <div className="mt-1">
                  <div className="group relative">
                    <p className="text-xs text-red-600 cursor-help">1 haftadır bekleyen servisler</p>
                    <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                      {services.filter(s => {
                        const sentDate = new Date(s.updated_at || s.created_at);
                        const oneWeekAgo = new Date();
                        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                        return s.status === 'servise-gonderildi' && sentDate < oneWeekAgo;
                      }).map(s => s.service_number).join(', ') || 'Geciken servis yok'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Kamera Arızaları - Güvenlik Kamera tedarikçisi ise en üstte göster */}
        {hasCameraCategory && (
          <div className="mb-8">
            {/* Camera Faults Stats */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Camera className="w-5 h-5 text-purple-600" />
                Atanmış Kamera Arızaları
              </h2>
              <button
                onClick={() => navigate('/supplier-camera-faults')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center text-sm"
              >
                <Eye className="w-4 h-4 mr-2" />
                Tümünü Yönet
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Camera className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Toplam</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{cameraFaultsStats.total}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Açık</p>
                    <p className="text-xl font-bold text-red-600">{cameraFaultsStats.open}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">İnceleniyor</p>
                    <p className="text-xl font-bold text-yellow-600">{cameraFaultsStats.in_progress}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Çözümlendi</p>
                    <p className="text-xl font-bold text-green-600">{cameraFaultsStats.resolved}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Arıza Tablosu */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Arızalı Kameralar</span>
                <select
                  value={cameraFaultsStatusFilter}
                  onChange={(e) => setCameraFaultsStatusFilter(e.target.value)}
                  className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                >
                  <option value="active">Aktif</option>
                  <option value="all">Tümü</option>
                  <option value="açık">Açık</option>
                  <option value="inceleniyor">İnceleniyor</option>
                  <option value="çözümlendi">Çözümlendi</option>
                  <option value="kapalı">Kapalı</option>
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Görsel</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Kamera</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Lokasyon</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden md:table-cell">Arıza</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Durum</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden md:table-cell">Tarih</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {cameraFaults.filter(fault => {
                      if (cameraFaultsStatusFilter === 'all') return true;
                      if (cameraFaultsStatusFilter === 'active') return fault.status !== 'çözümlendi' && fault.status !== 'kapalı';
                      return fault.status === cameraFaultsStatusFilter;
                    }).map((fault) => {
                      const previewImg = fault.camera_screenshot_path
                        ? `/uploads/${fault.camera_screenshot_path}`
                        : fault.image_path
                          ? `/uploads/${fault.image_path}`
                          : null;
                      return (
                      <tr key={fault.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          {previewImg ? (
                            <button
                              onClick={() => setDashboardImageModal(previewImg)}
                              className="block w-14 h-10 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:opacity-80 hover:ring-2 hover:ring-purple-400 transition-all flex-shrink-0"
                              title="Görseli büyüt"
                            >
                              <img src={previewImg} alt="Kamera" className="w-full h-full object-cover" />
                            </button>
                          ) : (
                            <div className="w-14 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                              <Camera className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{fault.camera_name}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {fault.location_name || '—'}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">{fault.fault_description}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            fault.status === 'açık' ? 'bg-red-100 text-red-800' :
                            fault.status === 'inceleniyor' ? 'bg-yellow-100 text-yellow-800' :
                            fault.status === 'çözümlendi' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
                          }`}>
                            {fault.status === 'açık' ? 'Açık' :
                             fault.status === 'inceleniyor' ? 'İnceleniyor' :
                             fault.status === 'çözümlendi' ? 'Çözümlendi' : fault.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400 hidden md:table-cell">
                          {new Date(fault.reported_at).toLocaleDateString('tr-TR')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {(fault.status === 'çözümlendi' || fault.status === 'kapalı') ? (
                            <button
                              onClick={() => fetchFaultHistory(fault)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-xs transition-colors"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              Detay
                            </button>
                          ) : (
                            <button
                              onClick={() => navigate('/supplier-camera-faults')}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900 text-xs transition-colors"
                            >
                              <Wrench className="w-3.5 h-3.5" />
                              Yönet
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
                {cameraFaults.filter(fault => {
                  if (cameraFaultsStatusFilter === 'all') return true;
                  if (cameraFaultsStatusFilter === 'active') return fault.status !== 'çözümlendi' && fault.status !== 'kapalı';
                  return fault.status === cameraFaultsStatusFilter;
                }).length === 0 && (
                  <div className="text-center py-8">
                    <Camera className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                      {cameraFaultsStatusFilter === 'active' ? 'Aktif kamera arızası bulunmuyor' : 'Bu filtreye uygun arıza yok'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Servis no, cihaz, şirket, lokasyon, seri no, envanter no, irtibat kişisi ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent w-full"
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">Tüm Durumlar</option>
            {allStatusOptions.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">Tüm Öncelikler</option>
            {priorityOptions.map(priority => (
              <option key={priority.value} value={priority.value}>{priority.label}</option>
            ))}
          </select>
        </div>

        {/* Services Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Atanmış Teknik Servisler</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Servis</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Şirket/Lokasyon</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cihaz</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Durum</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tarih</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">İşlemler</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredServices.map((service) => (
                  <tr key={service.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                          <Wrench className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{service.service_number}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{service.contact_person.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{service.company.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{service.location.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {service.device_name}
                        {service.device_count > 1 && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary-dark">
                            +{service.device_count - 1} cihaz
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 max-w-xs truncate">{service.fault_description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                        {getStatusLabel(service.status)}
                      </span>
                      {service.status === 'reddedildi' && service.rejection_reason && (
                        <div className="mt-1">
                          <div className="text-xs text-red-600 font-medium">Reddetme Sebebi:</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 max-w-xs">{service.rejection_reason}</div>
                          {service.rejected_at && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {new Date(service.rejected_at).toLocaleString('tr-TR')}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(service.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {service.status === 'tamamlandi' && (
                          <button
                            onClick={() => {
                              const token = localStorage.getItem('supplier_token');
                              window.open(`${import.meta.env.VITE_API_URL}/supplier-services/service/${service.id}/work-report?token=${token}`, '_blank');
                            }}
                            className="text-primary hover:text-primary-dark transition-colors"
                            title="İş Detaylarını Görüntüle"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        
                        {service.status === 'servise-gonderildi' && (
                          <button
                            onClick={() => {
                              setSelectedService(service);
                              setStatusData({ status: '', description: '' });
                              setShowStatusModal(true);
                            }}
                            className="text-primary hover:text-primary-dark transition-colors"
                            title="Durum Güncelle"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        
                        {service.status === 'servise-gonderildi' && (
                            <button
                            onClick={async () => {
                            setSelectedService(service);
                            const token = localStorage.getItem('supplier_token');
                            
                            try {
                              // Önce mevcut iş detaylarını kontrol et
                              const workDetailsResponse = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${service.id}/work-details`, {
                                headers: {
                                  'Authorization': `Bearer ${token}`
                                }
                              });
                              
                              if (workDetailsResponse.ok) {
                                const existingWorkDetails = await workDetailsResponse.json();
                                
                                if (existingWorkDetails.length > 0) {
                                  // Mevcut iş detayları var, onları yükle
                                  const formattedDevices = existingWorkDetails.map((device: any) => ({
                                    device_name: device.device_name,
                                    device_serial: device.device_serial || '',
                                    device_model: device.device_model || '',
                                    fault_description: device.fault_description || '',
                                    work_performed: device.work_performed || '',
                                    labor_hours: device.labor_hours || 0,
                                    labor_cost: device.labor_cost || 0,
                                    is_free_labor: device.is_free_labor || false,
                                    is_free_parts: device.is_free_parts || false,
                                    parts: device.parts?.length > 0 ? device.parts.map((part: any) => ({
                                      part_name: part.part_name || '',
                                      part_code: part.part_code || '',
                                      quantity: part.quantity || 1,
                                      unit_price: part.unit_price || 0,
                                      total_price: part.total_price || 0
                                    })) : [{
                                      part_name: '',
                                      part_code: '',
                                      quantity: 1,
                                      unit_price: 0,
                                      total_price: 0
                                    }]
                                  }));
                                  
                                  setWorkDevices(formattedDevices);
                                  setWorkData({
                                    currency: existingWorkDetails[0].currency || 'TRY',
                                    work_date: existingWorkDetails[0].work_date ? existingWorkDetails[0].work_date.split('T')[0] : new Date().toISOString().split('T')[0],
                                    notes: existingWorkDetails[0].notes || ''
                                  });
                                  
                                  // Seri numarası eksikse backend'den al
                                  const hasEmptySerial = formattedDevices.some(d => !d.device_serial);
                                  if (hasEmptySerial) {
                                    try {
                                      const deviceResponse = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${service.id}/devices`, {
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      if (deviceResponse.ok) {
                                        const serviceDevices = await deviceResponse.json();
                                        // Seri numaralarını güncelle
                                        formattedDevices.forEach((device, index) => {
                                          if (!device.device_serial && serviceDevices[index]) {
                                            device.device_serial = serviceDevices[index].device_serial || '';
                                          }
                                        });
                                        setWorkDevices([...formattedDevices]);
                                      }
                                    } catch (error) {
                                      console.error('Error loading serial numbers:', error);
                                    }
                                  }
                                }
                                
                                // Her durumda servis cihazlarını yükle (seri numarası için)
                                const deviceResponse = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${service.id}/devices`, {
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                });
                                
                                if (deviceResponse.ok && existingWorkDetails.length === 0) {
                                  // Sadece mevcut iş detayı yoksa cihaz bilgilerini set et
                                  const serviceDevices = await deviceResponse.json();
                                  if (serviceDevices.length > 0) {
                                      // Çoklu cihaz var
                                      setWorkDevices(serviceDevices.map((device: any) => {
                                        return {
                                          device_name: device.device_name,
                                          device_serial: device.device_serial || '',
                                          device_model: '',
                                          fault_description: device.fault_description || service.fault_description,
                                          work_performed: '',
                                          labor_hours: 0,
                                          labor_cost: 0,
                                          is_free_labor: false,
                                          is_free_parts: false,
                                          parts: [{
                                            part_name: '',
                                            part_code: '',
                                            quantity: 1,
                                            unit_price: 0,
                                            total_price: 0
                                          }]
                                        };
                                      }));
                                  } else {
                                    // Tek cihaz (eski sistem) - backend'den gelen seri numarasını kullan
                                    const deviceSerial = serviceDevices[0]?.device_serial || '';
                                    setWorkDevices([{
                                      device_name: service.device_name,
                                      device_serial: deviceSerial,
                                      device_model: '',
                                      fault_description: service.fault_description,
                                      work_performed: '',
                                      labor_hours: 0,
                                      labor_cost: 0,
                                      parts: [{
                                        part_name: '',
                                        part_code: '',
                                        quantity: 1,
                                        unit_price: 0,
                                        total_price: 0
                                      }]
                                    }]);
                                  }
                                }
                              }
                            } catch (error) {
                              console.error('Veri yüklenirken hata:', error);
                              // Hata durumunda tek cihaz varsayılan değerleri
                              setWorkDevices([{
                                device_name: service.device_name,
                                device_serial: '',
                                device_model: '',
                                fault_description: service.fault_description,
                                work_performed: '',
                                labor_hours: 0,
                                labor_cost: 0,
                                parts: [{
                                  part_name: '',
                                  part_code: '',
                                  quantity: 1,
                                  unit_price: 0,
                                  total_price: 0
                                }]
                              }]);
                            }
                            
                            // WorkData varsayılan değerlerle başlat (mevcut veri yoksa)
                            if (!workData.work_date) {
                              setWorkData({
                                currency: 'TRY',
                                work_date: new Date().toISOString().split('T')[0],
                                notes: ''
                              });
                            }
                            setShowWorkModal(true);
                          }}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="İş Detayı Ekle"
                          >
                            <Wrench className="w-4 h-4" />
                          </button>
                        )}
                        
                        {service.status === 'servise-gonderildi' && (
                          <>
                            <button
                              onClick={() => {
                                const token = localStorage.getItem('supplier_token');
                                window.open(`${import.meta.env.VITE_API_URL}/supplier-services/service/${service.id}/work-report?token=${token}`, '_blank');
                              }}
                              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200 transition-colors"
                              title="İş Raporu"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                const token = localStorage.getItem('supplier_token');
                                const params = new URLSearchParams({
                                  token: token || '',
                                  width: labelSettings.width.toString(),
                                  height: labelSettings.height.toString(),
                                  orientation: labelSettings.orientation,
                                  fontSize: labelSettings.fontSize.toString(),
                                  margin: labelSettings.margin.toString()
                                });
                                window.open(`${import.meta.env.VITE_API_URL}/supplier-services/service/${service.id}/cargo-label?${params}`, '_blank');
                              }}
                              className="text-orange-600 hover:text-orange-800 transition-colors"
                              title="Kargo Etiketi"
                            >
                              <Tag className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setShowLabelSettingsModal(true)}
                              className="text-primary hover:text-primary-dark transition-colors"
                              title="Etiket Ayarları"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        
                        {service.status === 'siparis-olusturuldu' && (
                          <button
                            onClick={() => {
                              if (confirm('Onarım tamamlandı ve müşteriye gönderildi olarak işaretlemek istiyor musunuz?')) {
                                const token = localStorage.getItem('supplier_token');
                                fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${service.id}/status`, {
                                  method: 'PUT',
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({
                                    status: 'onarildi-gonderildi',
                                    description: 'Onarım tamamlandı ve müşteriye gönderildi'
                                  })
                                }).then(() => {
                                  alert('Durum güncellendi!');
                                  loadServices();
                                });
                              }
                            }}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="Onarımı Tamamla"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        
                        {quoteStatus[service.id] && (
                          <>
                            <button
                              onClick={async () => {
                                setSelectedService(service);
                                const token = localStorage.getItem('supplier_token');
                                
                                try {
                                  // Mevcut iş detaylarını yükle
                                  const workDetailsResponse = await fetch(`${import.meta.env.VITE_API_URL}/supplier-services/service/${service.id}/work-details`, {
                                    headers: {
                                      'Authorization': `Bearer ${token}`
                                    }
                                  });
                                  
                                  if (workDetailsResponse.ok) {
                                    const existingWorkDetails = await workDetailsResponse.json();
                                    
                                    if (existingWorkDetails.length > 0) {
                                      // Mevcut iş detaylarını yükle
                                      const formattedDevices = existingWorkDetails.map((device: any) => ({
                                        device_name: device.device_name,
                                        device_serial: device.device_serial || '',
                                        device_model: device.device_model || '',
                                        fault_description: device.fault_description || '',
                                        work_performed: device.work_performed || '',
                                        labor_hours: device.labor_hours || 0,
                                        labor_cost: device.labor_cost || 0,
                                        is_free_labor: device.is_free_labor || false,
                                        is_free_parts: device.is_free_parts || false,
                                        parts: device.parts?.length > 0 ? device.parts.map((part: any) => ({
                                          part_name: part.part_name || '',
                                          part_code: part.part_code || '',
                                          quantity: part.quantity || 1,
                                          unit_price: part.unit_price || 0,
                                          total_price: part.total_price || 0
                                        })) : [{
                                          part_name: '',
                                          part_code: '',
                                          quantity: 1,
                                          unit_price: 0,
                                          total_price: 0
                                        }]
                                      }));
                                      
                                      setWorkDevices(formattedDevices);
                                      setWorkData({
                                        currency: existingWorkDetails[0].currency || 'TRY',
                                        work_date: existingWorkDetails[0].work_date ? existingWorkDetails[0].work_date.split('T')[0] : new Date().toISOString().split('T')[0],
                                        notes: existingWorkDetails[0].notes || ''
                                      });
                                      setShowWorkModal(true);
                                    }
                                  }
                                } catch (error) {
                                  console.error('Teklif yüklenirken hata:', error);
                                  alert('Teklif bilgileri yüklenemedi');
                                }
                              }}
                              className="text-primary hover:text-primary-dark transition-colors"
                              title="Teklifi Düzenle"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteQuote(service.id)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                              title="Teklifi Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                const token = localStorage.getItem('supplier_token');
                                const params = new URLSearchParams({
                                  token: token || '',
                                  width: labelSettings.width.toString(),
                                  height: labelSettings.height.toString(),
                                  orientation: labelSettings.orientation,
                                  fontSize: labelSettings.fontSize.toString(),
                                  margin: labelSettings.margin.toString()
                                });
                                window.open(`${import.meta.env.VITE_API_URL}/supplier-services/service/${service.id}/cargo-label?${params}`, '_blank');
                              }}
                              className="text-orange-600 hover:text-orange-800 transition-colors"
                              title="Kargo Etiketi"
                            >
                              <Tag className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredServices.length === 0 && (
              <div className="text-center py-12">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 dark:text-gray-400">Henüz atanmış teknik servis bulunmuyor</p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Kamera Görsel Modal */}
      {dashboardImageModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[70] p-4"
          onClick={() => setDashboardImageModal(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setDashboardImageModal(null)}
              className="absolute top-3 right-3 p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 z-10"
            >
              <span className="text-gray-800 dark:text-gray-200 text-xl font-bold leading-none">&times;</span>
            </button>
            <img
              src={dashboardImageModal}
              alt="Kamera Görüntüsü"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Fault History Modal */}
      <Modal isOpen={showFaultHistoryModal} onClose={() => setShowFaultHistoryModal(false)}>
          {selectedFaultForHistory && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Arıza Detayları</h3>
                <button
                  onClick={() => setShowFaultHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{selectedFaultForHistory.camera_name} - {selectedFaultForHistory.location_name}</p>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Özet Bilgiler */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-primary/10 rounded-lg p-4">
                  <div className="text-sm text-primary font-medium">Çözüm Süresi</div>
                  <div className="text-2xl font-bold text-primary-dark">
                    {selectedFaultForHistory.supplier_completed_at
                      ? `${calculateDaysBetween(selectedFaultForHistory.reported_at, selectedFaultForHistory.supplier_completed_at)} gün`
                      : 'Devam ediyor'}
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-sm text-green-600 font-medium">Durum</div>
                  <div className="text-2xl font-bold text-green-700">
                    {selectedFaultForHistory.status === 'çözümlendi' ? 'Çözümlendi' :
                     selectedFaultForHistory.status === 'kapalı' ? 'Kapalı' : selectedFaultForHistory.status}
                  </div>
                </div>
              </div>

              {/* Arıza Bilgileri */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Arıza Açıklaması</h4>
                <p className="text-gray-700 dark:text-gray-300">{selectedFaultForHistory.fault_description}</p>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Bildirim Tarihi: {new Date(selectedFaultForHistory.reported_at).toLocaleDateString('tr-TR', {
                    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </div>
              </div>

              {/* Çözüm Açıklaması */}
              {selectedFaultForHistory.supplier_solution && (
                <div className="bg-green-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-green-900 mb-2">Çözüm Açıklaması</h4>
                  <p className="text-green-800">{selectedFaultForHistory.supplier_solution}</p>
                </div>
              )}

              {/* Yapılan İşlemler */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Yapılan İşlemler</h4>
                {faultHistoryActions.length > 0 ? (
                  <div className="space-y-3">
                    {faultHistoryActions.map((action) => (
                      <div key={action.id} className="border-l-4 border-purple-500 pl-4 py-2">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900 dark:text-gray-100">{getActionTypeLabel(action.action_type)}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(action.created_at).toLocaleDateString('tr-TR', {
                              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                        {action.old_status && action.new_status && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {action.old_status} → {action.new_status}
                          </div>
                        )}
                        {action.description && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{action.description}</p>
                        )}
                        {action.solution_text && (
                          <p className="text-sm text-green-700 mt-1">{action.solution_text}</p>
                        )}
                        {action.supplier_user_name && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">İşlemi yapan: {action.supplier_user_name}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Henüz kayıtlı işlem bulunmuyor.</p>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <button
                onClick={() => setShowFaultHistoryModal(false)}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Status Update Modal */}
      <Modal isOpen={showStatusModal} onClose={() => setShowStatusModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">Durum Güncelle</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Yeni Durum
                </label>
                <select
                  value={statusData.status}
                  onChange={(e) => setStatusData({ ...statusData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Durum seçin</option>
                  {supplierStatusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Açıklama
                </label>
                <textarea
                  value={statusData.description}
                  onChange={(e) => setStatusData({ ...statusData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Durum değişikliği açıklaması..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
              >
                İptal
              </button>
              <button
                onClick={handleStatusUpdate}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Güncelle
              </button>
            </div>
          </div>
        </Modal>

      {/* Quote Upload Modal */}
      <Modal isOpen={showQuoteModal} onClose={() => setShowQuoteModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">Teklif Yükle</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tutar
                  </label>
                  <input
                    type="number"
                    value={quoteData.quote_amount}
                    onChange={(e) => setQuoteData({ ...quoteData, quote_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Para Birimi
                  </label>
                  <select
                    value={quoteData.quote_currency}
                    onChange={(e) => setQuoteData({ ...quoteData, quote_currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Teklif Dosyası
                </label>
                <input
                  type="file"
                  onChange={(e) => setQuoteData({ ...quoteData, quote_file: e.target.files?.[0] || null })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Açıklama
                </label>
                <textarea
                  value={quoteData.description}
                  onChange={(e) => setQuoteData({ ...quoteData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Teklif açıklaması..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowQuoteModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
              >
                İptal
              </button>
              <button
                onClick={handleQuoteUpload}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Yükle
              </button>
            </div>
          </div>
        </Modal>

      {/* Work Detail Modal */}
      <Modal isOpen={showWorkModal} onClose={() => setShowWorkModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-6 w-full max-w-6xl max-h-[98vh] sm:max-h-[95vh] overflow-y-auto">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6">
              <h3 className="text-lg font-medium mb-2 sm:mb-0">İş Detayı Ekle</h3>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Servis kaydındaki cihazlar için onarım bilgilerini girin
              </div>
            </div>
            
            {/* Para Birimi Seçimi */}
            <div className="bg-primary/10 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Para Birimi *
                </label>
                <select
                  value={workData.currency}
                  onChange={(e) => setWorkData({ ...workData, currency: e.target.value })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 text-sm"
                >
                  <option value="TRY">🇹🇷 Türk Lirası (TRY)</option>
                  <option value="USD">🇺🇸 Amerikan Doları (USD)</option>
                  <option value="EUR">🇪🇺 Euro (EUR)</option>
                </select>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Tüm fiyatlar bu para biriminde girilecek
                </div>
              </div>
            </div>
            
            {/* Devices */}
            <div className="space-y-4 sm:space-y-6 mb-4 sm:mb-6">
              <div className="mb-3 sm:mb-4">
                <h4 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100">Servis Kaydındaki Cihazlar ({workDevices.length})</h4>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Bu servise ait tüm cihazlar için onarım bilgilerini girin</p>
              </div>
              {workDevices.map((device, deviceIndex) => {
                const isExpanded = expandedDevices[deviceIndex];
                const deviceTitle = device.device_name || `Cihaz ${deviceIndex + 1}`;
                
                return (
                <div key={deviceIndex} className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700">
                  <div 
                    className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                    onClick={() => toggleDeviceExpanded(deviceIndex)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`transform transition-transform duration-200 ${
                        isExpanded ? 'rotate-90' : 'rotate-0'
                      }`}>
                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      <h4 className="text-md font-medium text-gray-900 dark:text-gray-100">{deviceTitle}</h4>
                      {device.device_serial && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 bg-green-100 px-2 py-1 rounded">
                          Seri: {device.device_serial}
                        </span>
                      )}
                      {device.is_free_labor && (
                        <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded font-medium">
                          Bedelsiz İşçilik
                        </span>
                      )}
                      {device.is_free_parts && (
                        <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded font-medium">
                          Bedelsiz Parça
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                      Servis kaydından
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cihaz Adı
                      </label>
                      <input
                        type="text"
                        value={device.device_name}
                        onChange={(e) => updateDevice(deviceIndex, 'device_name', e.target.value)}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-xs sm:text-sm"
                        placeholder="Servis kaydından alınacak"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Seri No
                      </label>
                      <input
                        type="text"
                        value={device.device_serial}
                        onChange={(e) => updateDevice(deviceIndex, 'device_serial', e.target.value)}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
                        placeholder="Cihaz seri numarası"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Arıza Açıklaması (Servis Kaydından)
                      </label>
                      <textarea
                        value={device.fault_description}
                        readOnly
                        rows={2}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-xs sm:text-sm"
                        placeholder="Servis kaydından alınacak"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Yapılacak Onarım İşlemi *
                      </label>
                      <textarea
                        value={device.work_performed}
                        onChange={(e) => updateDevice(deviceIndex, 'work_performed', e.target.value)}
                        rows={2}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
                        placeholder="Yapılan onarım işlemlerini detaylı olarak açıklayın..."
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Çalışma Saati
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        value={device.labor_hours}
                        onChange={(e) => updateDevice(deviceIndex, 'labor_hours', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        İşçilik Maliyeti
                      </label>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={device.is_free_labor === true}
                            onChange={(e) => {
                              e.stopPropagation();
                              const checked = e.target.checked;
                              const updatedDevices = [...workDevices];
                              updatedDevices[deviceIndex].is_free_labor = checked;
                              if (checked) {
                                updatedDevices[deviceIndex].labor_cost = 0;
                              }
                              setWorkDevices(updatedDevices);
                            }}
                            className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 focus:ring-2"
                          />
                          <span className="text-xs sm:text-sm text-green-600 font-medium">
                            Bedelsiz İşçilik
                          </span>
                        </div>
                        {device.is_free_labor ? (
                          <div className="text-xs sm:text-sm text-green-600 bg-green-50 p-2 rounded border">
                            ✓ Bu cihaz için bedelsiz onarım yapılacak
                          </div>
                        ) : (
                          <input
                            type="number"
                            step="0.01"
                            value={device.labor_cost}
                            onChange={(e) => updateDevice(deviceIndex, 'labor_cost', parseFloat(e.target.value) || 0)}
                            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
                            placeholder="İşçilik maliyeti"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Parts */}
                  <div className="border-t pt-3 sm:pt-4">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 space-y-2 sm:space-y-0">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                        <h5 className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">Parçalar</h5>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={device.is_free_parts === true}
                            onChange={(e) => {
                              e.stopPropagation();
                              const checked = e.target.checked;
                              const updatedDevices = [...workDevices];
                              updatedDevices[deviceIndex].is_free_parts = checked;
                              if (checked) {
                                updatedDevices[deviceIndex].parts.forEach(part => {
                                  part.unit_price = 0;
                                  part.total_price = 0;
                                });
                              }
                              setWorkDevices(updatedDevices);
                            }}
                            className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 focus:ring-2"
                          />
                          <span className="text-xs sm:text-sm text-green-600 font-medium">
                            Bedelsiz Parça
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => addPart(deviceIndex)}
                        className="flex items-center px-2 py-1 bg-primary text-white rounded text-xs hover:bg-primary-dark self-start sm:self-auto"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Parça Ekle
                      </button>
                    </div>
                    
                    <div className="hidden sm:grid sm:grid-cols-6 gap-2 mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                      <div>Parça Adı</div>
                      <div>Parça Kodu</div>
                      <div>Adet</div>
                      <div>Birim Fiyat ({workData.currency})</div>
                      <div>Toplam ({workData.currency})</div>
                      <div>İşlem</div>
                    </div>
                    {device.parts.map((part, partIndex) => {
                      const nameKey = `${deviceIndex}-${partIndex}-part_name`;
                      const codeKey = `${deviceIndex}-${partIndex}-part_code`;
                      
                      return (
                      <div key={partIndex} className="mb-2 p-2 bg-white dark:bg-gray-800 rounded border">
                        {/* Mobil görünüm */}
                        <div className="block sm:hidden space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Parça #{partIndex + 1}</span>
                            {device.parts.length > 1 && (
                              <button
                                onClick={() => removePart(deviceIndex, partIndex)}
                                className="text-red-600 hover:text-red-800 p-1"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          
                          <div className="relative">
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Parça Adı</label>
                            <input
                              type="text"
                              value={part.part_name}
                              onChange={(e) => {
                                updatePart(deviceIndex, partIndex, 'part_name', e.target.value);
                                getPartSuggestions(e.target.value, 'name', nameKey);
                              }}
                              onFocus={() => {
                                if (part.part_name.length >= 3) {
                                  getPartSuggestions(part.part_name, 'name', nameKey);
                                }
                              }}
                              onBlur={() => {
                                setTimeout(() => setShowSuggestions(prev => ({ ...prev, [nameKey]: false })), 200);
                              }}
                              placeholder="Parça Adı"
                              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                            />
                            {showSuggestions[nameKey] && partSuggestions[nameKey]?.length > 0 && (
                              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-32 overflow-y-auto">
                                {partSuggestions[nameKey].map((suggestion, idx) => (
                                  <div
                                    key={idx}
                                    onClick={() => selectPartSuggestion(suggestion, deviceIndex, partIndex, 'part_name')}
                                    className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 cursor-pointer text-xs"
                                  >
                                    {suggestion}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="relative">
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Parça Kodu</label>
                            <input
                              type="text"
                              value={part.part_code}
                              onChange={(e) => {
                                updatePart(deviceIndex, partIndex, 'part_code', e.target.value);
                                getPartSuggestions(e.target.value, 'code', codeKey);
                              }}
                              onFocus={() => {
                                if (part.part_code.length >= 3) {
                                  getPartSuggestions(part.part_code, 'code', codeKey);
                                }
                              }}
                              onBlur={() => {
                                setTimeout(() => setShowSuggestions(prev => ({ ...prev, [codeKey]: false })), 200);
                              }}
                              placeholder="Parça Kodu"
                              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                            />
                            {showSuggestions[codeKey] && partSuggestions[codeKey]?.length > 0 && (
                              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-32 overflow-y-auto">
                                {partSuggestions[codeKey].map((suggestion, idx) => (
                                  <div
                                    key={idx}
                                    onClick={() => selectPartSuggestion(suggestion, deviceIndex, partIndex, 'part_code')}
                                    className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 cursor-pointer text-xs"
                                  >
                                    {suggestion}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Adet</label>
                              <input
                                type="number"
                                value={part.quantity}
                                onChange={(e) => updatePart(deviceIndex, partIndex, 'quantity', parseFloat(e.target.value) || 1)}
                                placeholder="Adet"
                                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Birim Fiyat</label>
                              <input
                                type="number"
                                step="0.01"
                                value={part.unit_price}
                                onChange={(e) => updatePart(deviceIndex, partIndex, 'unit_price', parseFloat(e.target.value) || 0)}
                                placeholder={device.is_free_parts ? "Bedelsiz" : "Birim Fiyat"}
                                disabled={device.is_free_parts}
                                className={`w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                                  device.is_free_parts ? 'bg-green-50 text-green-600' : ''
                                }`}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Toplam</label>
                              <input
                                type="number"
                                step="0.01"
                                value={part.total_price}
                                readOnly
                                placeholder={device.is_free_parts ? "Bedelsiz" : "Toplam"}
                                className={`w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm ${
                                  device.is_free_parts ? 'bg-green-50 text-green-600' : 'bg-gray-100 dark:bg-gray-700'
                                }`}
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Desktop görünüm */}
                        <div className="hidden sm:grid sm:grid-cols-6 gap-2">
                          <div className="relative">
                            <input
                              type="text"
                              value={part.part_name}
                              onChange={(e) => {
                                updatePart(deviceIndex, partIndex, 'part_name', e.target.value);
                                getPartSuggestions(e.target.value, 'name', nameKey);
                              }}
                              onFocus={() => {
                                if (part.part_name.length >= 3) {
                                  getPartSuggestions(part.part_name, 'name', nameKey);
                                }
                              }}
                              onBlur={() => {
                                setTimeout(() => setShowSuggestions(prev => ({ ...prev, [nameKey]: false })), 200);
                              }}
                              placeholder="Parça Adı"
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                            />
                            {showSuggestions[nameKey] && partSuggestions[nameKey]?.length > 0 && (
                              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-32 overflow-y-auto">
                                {partSuggestions[nameKey].map((suggestion, idx) => (
                                  <div
                                    key={idx}
                                    onClick={() => selectPartSuggestion(suggestion, deviceIndex, partIndex, 'part_name')}
                                    className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 cursor-pointer text-xs"
                                  >
                                    {suggestion}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="relative">
                            <input
                              type="text"
                              value={part.part_code}
                              onChange={(e) => {
                                updatePart(deviceIndex, partIndex, 'part_code', e.target.value);
                                getPartSuggestions(e.target.value, 'code', codeKey);
                              }}
                              onFocus={() => {
                                if (part.part_code.length >= 3) {
                                  getPartSuggestions(part.part_code, 'code', codeKey);
                                }
                              }}
                              onBlur={() => {
                                setTimeout(() => setShowSuggestions(prev => ({ ...prev, [codeKey]: false })), 200);
                              }}
                              placeholder="Parça Kodu"
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                            />
                            {showSuggestions[codeKey] && partSuggestions[codeKey]?.length > 0 && (
                              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-32 overflow-y-auto">
                                {partSuggestions[codeKey].map((suggestion, idx) => (
                                  <div
                                    key={idx}
                                    onClick={() => selectPartSuggestion(suggestion, deviceIndex, partIndex, 'part_code')}
                                    className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 cursor-pointer text-xs"
                                  >
                                    {suggestion}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div>
                            <input
                              type="number"
                              value={part.quantity}
                              onChange={(e) => updatePart(deviceIndex, partIndex, 'quantity', parseFloat(e.target.value) || 1)}
                              placeholder="Adet"
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                            />
                          </div>
                          <div>
                            <input
                              type="number"
                              step="0.01"
                              value={part.unit_price}
                              onChange={(e) => updatePart(deviceIndex, partIndex, 'unit_price', parseFloat(e.target.value) || 0)}
                              placeholder={device.is_free_parts ? "Bedelsiz" : "Birim Fiyat"}
                              disabled={device.is_free_parts}
                              className={`w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                                device.is_free_parts ? 'bg-green-50 text-green-600' : ''
                              }`}
                            />
                          </div>
                          <div>
                            <input
                              type="number"
                              step="0.01"
                              value={part.total_price}
                              readOnly
                              placeholder={device.is_free_parts ? "Bedelsiz" : "Toplam"}
                              className={`w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs ${
                                device.is_free_parts ? 'bg-green-50 text-green-600' : 'bg-gray-100 dark:bg-gray-700'
                              }`}
                            />
                          </div>
                          <div className="flex justify-center">
                            {device.parts.length > 1 && (
                              <button
                                onClick={() => removePart(deviceIndex, partIndex)}
                                className="text-red-600 hover:text-red-800 p-1"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  
                      <div className="mt-3 text-center sm:text-right">
                        <div className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-2 rounded">
                          Cihaz Toplam: {calculateDeviceTotal(device).toFixed(2)} {workData.currency}
                        </div>
                        {(device.is_free_labor || device.is_free_parts) && (
                          <div className="mt-2 flex flex-wrap justify-center sm:justify-end gap-1">
                            {device.is_free_labor && (
                              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                Bedelsiz İşçilik
                              </span>
                            )}
                            {device.is_free_parts && (
                              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                Bedelsiz Parça
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
            
            {/* General Info */}
            <div className="bg-gray-50 dark:bg-gray-700 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6">
              <h4 className="text-sm sm:text-md font-medium text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">Genel Bilgiler</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Çalışma Tarihi
                  </label>
                  <input
                    type="date"
                    value={workData.work_date}
                    onChange={(e) => setWorkData({ ...workData, work_date: e.target.value })}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <div className="text-sm sm:text-lg font-bold text-purple-600">
                    Genel Toplam: {calculateGrandTotal().toFixed(2)} {workData.currency}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Genel Notlar
              </label>
              <textarea
                value={workData.notes}
                onChange={(e) => setWorkData({ ...workData, notes: e.target.value })}
                rows={3}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
                placeholder="Genel notlar ve açıklamalar..."
              />
            </div>
            
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => setShowWorkModal(false)}
                className="px-3 sm:px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 text-sm"
              >
                İptal
              </button>
              <button
                onClick={handleWorkDetailAdd}
                className="px-3 sm:px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark text-sm"
              >
                Kaydet
              </button>
              <button
                onClick={handleWorkDetailAndQuote}
                className="px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
              >
                Kaydet ve Teklif Ver
              </button>
            </div>
          </div>
        </Modal>

      {/* Label Settings Modal */}
      <Modal isOpen={showLabelSettingsModal} onClose={() => setShowLabelSettingsModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Kargo Etiketi Ayarları
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Genişlik (mm)
                  </label>
                  <input
                    type="number"
                    value={labelSettings.width}
                    onChange={(e) => setLabelSettings({...labelSettings, width: parseInt(e.target.value) || 100})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="50"
                    max="200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Yükseklik (mm)
                  </label>
                  <input
                    type="number"
                    value={labelSettings.height}
                    onChange={(e) => setLabelSettings({...labelSettings, height: parseInt(e.target.value) || 75})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="30"
                    max="150"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Yönlendirme
                </label>
                <select
                  value={labelSettings.orientation}
                  onChange={(e) => setLabelSettings({...labelSettings, orientation: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="portrait">Dikey (Portrait)</option>
                  <option value="landscape">Yatay (Landscape)</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Font Boyutu (px)
                  </label>
                  <input
                    type="number"
                    value={labelSettings.fontSize}
                    onChange={(e) => setLabelSettings({...labelSettings, fontSize: parseInt(e.target.value) || 8})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="6"
                    max="16"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Kenar Boşluğu (mm)
                  </label>
                  <input
                    type="number"
                    value={labelSettings.margin}
                    onChange={(e) => setLabelSettings({...labelSettings, margin: parseInt(e.target.value) || 2})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="1"
                    max="10"
                  />
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Hazır Ayarlar:</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => setLabelSettings({width: 100, height: 75, orientation: 'portrait', fontSize: 8, margin: 2})}
                    className="w-full text-left px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
                  >
                    Zebra ZT411 (100x75mm)
                  </button>
                  <button
                    onClick={() => setLabelSettings({width: 80, height: 60, orientation: 'portrait', fontSize: 7, margin: 2})}
                    className="w-full text-left px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
                  >
                    Standart Etiket (80x60mm)
                  </button>
                  <button
                    onClick={() => setLabelSettings({width: 120, height: 80, orientation: 'landscape', fontSize: 9, margin: 3})}
                    className="w-full text-left px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
                  >
                    Büyük Etiket (120x80mm)
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowLabelSettingsModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  setShowLabelSettingsModal(false);
                  alert('Etiket ayarları kaydedildi');
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
              >
                Kaydet
              </button>
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default SupplierDashboard;